import {Box, Button, FormControlLabel, Switch, TextField, Typography} from '@mui/material';
import {Check, ExitToApp, PlayArrow, Share, Tv} from '@mui/icons-material';
import {useNavigate, useParams} from 'react-router-dom';
import CopyButton from '../common/CopyButton';

export default function LobbyControls({
  isGameMaster,
  selectedQuiz,
  currentPlayerReady,
  allPlayersReady,
  onToggleReady,
  onStartGame,
  onLeaveLobby,
  players,
  gameMasterParticipates,
  onToggleGameMasterParticipation
}) {
  const { lobbyId } = useParams();
  const navigate = useNavigate();
  const gameMaster = Object.values(players || {}).find(player => player.isGameMaster);
  const gameMasterName = gameMaster?.displayName || 'Unbekannt';

  const handleShare = async () => {
    const shareData = {
      title: 'Quiz-Lobby beitreten',
      text: `Tritt meiner Quiz-Lobby bei! Code: ${lobbyId}`,
      url: window.location.href
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        navigator.clipboard.writeText(lobbyId);
      }
    } catch (error) {
      console.error('Error sharing:', error);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      navigate(`/lobby/${lobbyId}`);
    }
  };

  return (
    <Box className="lobby-controls">
      <Box className="lobby-header">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '4rem', flex: 1 }}>
          <Typography variant="h6">
            Lobby von {gameMasterName}
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              value={lobbyId}
              size="small"
              variant="outlined"
              onKeyPress={handleKeyPress}
              InputProps={{
                readOnly: true,
                sx: {
                  fontFamily: 'Roboto Mono, monospace'
                }
              }}
              sx={{ width: '120px' }}
            />
            <CopyButton text={lobbyId} />
          </Box>

          <Button
            variant="outlined"
            startIcon={<Share />}
            onClick={handleShare}
            sx={{
              borderColor: 'var(--color-secondary)',
              color: 'var(--color-secondary)',
              '&:hover': {
                borderColor: 'var(--color-secondary-light)',
                color: 'var(--color-secondary-light)',
                background: 'rgba(203, 126, 0, 0.1)'
              }
            }}
          >
            Freunde einladen
          </Button>
        </Box>

        <Button
          variant="outlined"
          color="error"
          onClick={onLeaveLobby}
          startIcon={<ExitToApp />}
          className="button-glow"
          sx={{
            borderColor: 'rgba(211, 47, 47, 0.5)',
            color: '#f44336',
            '&:hover': {
              borderColor: '#f44336',
              background: 'rgba(244, 67, 54, 0.1)'
            }
          }}
        >
          Lobby verlassen
        </Button>
      </Box>

      <Box className="box-divider" />

      <Box className="lobby-game-actions" sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button
          variant="contained"
          color={currentPlayerReady ? "success" : "primary"}
          onClick={onToggleReady}
          startIcon={currentPlayerReady ? <Check /> : null}
          className="button-glow"
          sx={{
            background: currentPlayerReady ? 
              'linear-gradient(45deg, #2e7d32 30%, #43a047 90%)' : 
              'var(--color-secondary)',
            color: '#fff',
            '&:hover': {
              background: currentPlayerReady ? 
                'linear-gradient(45deg, #1b5e20 30%, #2e7d32 90%)' : 
                'var(--color-secondary-light)',
              color: '#fff'
            }
          }}
        >
          {currentPlayerReady ? "Bereit" : "Bereit machen"}
        </Button>

        {isGameMaster && selectedQuiz && currentPlayerReady && (
          <Button
            variant="contained"
            onClick={onStartGame}
            disabled={!allPlayersReady}
            startIcon={<PlayArrow />}
            className="button-glow"
            sx={{
              background: 'var(--color-secondary)',
              '&:hover': {
                background: 'var(--color-secondary-light)'
              },
              '&.Mui-disabled': {
                background: 'rgba(255, 255, 255, 0.12)',
                color: 'rgba(255, 255, 255, 0.3)'
              }
            }}
          >
            Spiel starten
          </Button>
        )}
        
        {isGameMaster && (
          <FormControlLabel
            control={
              <Switch
                checked={!gameMasterParticipates}
                onChange={onToggleGameMasterParticipation}
                color="primary"
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Tv fontSize="small" />
                <Typography variant="body2">Presenter-Modus</Typography>
              </Box>
            }
            sx={{ ml: 1 }}
          />
        )}
      </Box>
    </Box>
  );
} 